import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Divider,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Paper
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import apiService from '../../services/api';
import { formatDate } from '../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';

const ManageBillingSection: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch subscription data from the backend
    const fetchSubscriptionInfo = async () => {
      setLoading(true);
      try {
        const data = await apiService.getSubscriptionInfo();
        setSubscriptionInfo(data);
        
        // Clear any previous errors when we successfully load data
        setError(null);
      } catch (err: any) {
        // Don't show error for organizations without a customer ID, this is normal for trial orgs
        if (err.response?.data?.error?.includes('does not have a Stripe customer ID')) {
          setSubscriptionInfo(null);
        } else {
          setError(err.response?.data?.error || 'Failed to fetch subscription information');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchSubscriptionInfo();
  }, []);

  const handleManageSubscription = async () => {
    setLoading(true);
    try {
      const response = await apiService.createBillingPortalSession(
        window.location.origin + '/settings?section=billing'
      );
      
      if (response && response.url) {
        // Redirect to Stripe's billing portal
        window.location.href = response.url;
      } else {
        setError('Failed to generate billing portal link');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to access billing portal');
    } finally {
      setLoading(false);
    }
  };

  const handleViewBillingHistory = async () => {
    setLoading(true);
    try {
      // The billing portal has both subscription management and invoice history
      // We'll use the same endpoint but direct users back to settings
      const response = await apiService.createBillingPortalSession(
        window.location.origin + '/settings?section=billing'
      );
      
      if (response && response.url) {
        // Redirect to Stripe's billing portal
        // Stripe doesn't support directly opening the invoice history tab,
        // but users can easily navigate to it from the portal
        window.location.href = response.url;
      } else {
        setError('Failed to generate billing history link');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to access billing history');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPlans = () => {
    navigate('/settings/plans');
  };

  // Helper function to determine status display label
  const getStatusLabel = (info: any) => {
    if (info.organizationStatus === 'ACTIVE_UNTIL') return 'Cancelling Soon';
    if (info.organizationStatus === 'CANCELLED') return 'Cancelled';
    if (info.status === 'active') return 'Active';
    if (info.status === 'past_due') return 'Past Due';
    if (info.status === 'unpaid') return 'Unpaid';
    return 'Inactive';
  };
  
  // Helper function to determine status color
  const getStatusColor = (info: any) => {
    if (info.organizationStatus === 'ACTIVE_UNTIL') return 'warning';
    if (info.organizationStatus === 'CANCELLED') return 'error';
    if (info.status === 'active') return 'success';
    if (info.status === 'past_due') return 'warning';
    return 'error'; // Default for inactive, unpaid, etc.
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Subscription Management
      </Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      {/* Show a warning message for trial organizations without a Stripe customer ID */}
      {!subscriptionInfo?.hasSubscription && !subscriptionInfo?.stripeCustomerId && subscriptionInfo?.plan !== 'INTERNAL' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Organization does not have a Stripe customer ID. Please subscribe first.
        </Alert>
      )}

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Current Plan
                </Typography>
                
                {subscriptionInfo && subscriptionInfo.hasSubscription ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Chip 
                        label={getStatusLabel(subscriptionInfo)} 
                        color={getStatusColor(subscriptionInfo)}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <Typography variant="body1">
                        {subscriptionInfo.planName}
                      </Typography>
                    </Box>
                    
                    {subscriptionInfo.cancelAtPeriodEnd && (
                      <Alert severity="warning" sx={{ mt: 1, mb: 1 }} icon={false}>
                        <Typography variant="body2">
                          Your subscription will be cancelled on {formatDate(new Date(subscriptionInfo.currentPeriodEnd * 1000))}
                        </Typography>
                      </Alert>
                    )}
                    
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      ${subscriptionInfo.amount/100}/{subscriptionInfo.interval}
                    </Typography>
                    
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Seats:</strong> {subscriptionInfo.seatsUsed || 0}/
                        {subscriptionInfo.plan === 'INTERNAL' ? 'Unlimited' : subscriptionInfo.seatLimit || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Accounts:</strong> {subscriptionInfo.accountsUsed || 0}/
                        {subscriptionInfo.plan === 'INTERNAL' ? 'Unlimited' : subscriptionInfo.accountLimit || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Simulations remaining:</strong> {
                          subscriptionInfo.plan === 'INTERNAL' ? 'Unlimited' : 
                          (subscriptionInfo.monthlySimQuota || 0) - (subscriptionInfo.simUsed || 0)
                        }
                      </Typography>
                      {subscriptionInfo.currentPeriodEnd && subscriptionInfo.organizationStatus !== 'ACTIVE_UNTIL' && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Renewal date:</strong> {formatDate(new Date(subscriptionInfo.currentPeriodEnd * 1000))}
                        </Typography>
                      )}
                    </Box>
                  </>
                ) : (
                  <Box>
                    <Typography variant="body1" gutterBottom>
                      {subscriptionInfo?.plan === 'INTERNAL' ? 'Internal Plan' : 'Trial Plan'}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Seats:</strong> {subscriptionInfo?.seatsUsed || 0}/
                        {subscriptionInfo?.plan === 'INTERNAL' ? 'Unlimited' : subscriptionInfo?.seatLimit || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Accounts:</strong> {subscriptionInfo?.accountsUsed || 0}/
                        {subscriptionInfo?.plan === 'INTERNAL' ? 'Unlimited' : subscriptionInfo?.accountLimit || 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Simulations remaining:</strong> {
                          subscriptionInfo?.plan === 'INTERNAL' ? 'Unlimited' : 
                          (subscriptionInfo?.monthlySimQuota || 0) - (subscriptionInfo?.simUsed || 0)
                        }
                      </Typography>
                      {subscriptionInfo?.trialEndsAt && subscriptionInfo?.plan !== 'INTERNAL' && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Trial expires:</strong> {formatDate(new Date(subscriptionInfo.trialEndsAt))}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>
            
            {subscriptionInfo?.plan !== 'INTERNAL' && (
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Actions
                  </Typography>
                  
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1, justifyContent: 'center' }}>
                    {subscriptionInfo && subscriptionInfo.hasSubscription ? (
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<CreditCardIcon />}
                        onClick={handleManageSubscription}
                        disabled={loading}
                        fullWidth
                      >
                        Manage Subscription
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<CreditCardIcon />}
                        onClick={handleViewPlans}
                        disabled={loading}
                        fullWidth
                      >
                        View Plans
                      </Button>
                    )}
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
      
      <Card>
        <CardContent>
          <Typography variant="body1" gutterBottom>
            Need help with billing?
          </Typography>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            For any billing-related questions or assistance, please contact our support team at 
            <Box component="span" sx={{ fontWeight: 'bold', ml: 0.5 }}>
              support@wavescoop.com
            </Box>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ManageBillingSection; 